import { Modal } from "bootstrap";
import { noNullQuerySelector } from "../src/utils/dom";

const createFilterElement = (filterName: string, id: string, value: string) => {
  const filter = document.createElement("button");
  filter.id = `${filterName}-${id}-filter`;
  filter.type = "button";
  filter.classList.value = `status-tag primary gap-2 ${filterName}-${id}`;
  filter.innerHTML = `
    <span>${value}</span>
    <i
      data-target=".${filterName}-${id}"
      class="clear-${filterName} bi bi-x-circle-fill"
      aria-hidden="true"
    ></i>
  `;

  return filter;
};

const createFilterInput = (filterName: string, id: string) => {
  const input = document.createElement("input");
  input.type = "hidden";
  input.className = `${filterName}-${id}`;
  input.name = `${filterName}s[]`;
  input.value = id;

  return input;
};

const addFilterToContainer = (
  filterName: string,
  id: string,
  value: string,
) => {
  if (id === "") return false;

  document
    .querySelectorAll<HTMLElement>(
      `.${filterName}-${id}, #${filterName}-${id}-filter`,
    )
    .forEach((element) => element.remove());

  const container = document.getElementById(`${filterName}s-container`);

  container?.appendChild(createFilterElement(filterName, id, value));
  container?.appendChild(createFilterInput(filterName, id));
};

const addSubject = (id: string, name: string) => {
  if (id === "") return false;

  addFilterToContainer("subject", id, name);
  addSubjectListeners();
};

const addLanguage = (id: string, name: string) => {
  if (id === "") return false;

  addFilterToContainer("language", id, name);
  addLanguageListeners();
};

const addSubjectListeners = () => {
  document
    .querySelectorAll<HTMLButtonElement>(".clear-subject:not([data-listened])")
    .forEach((element) => {
      const parent = element.parentNode as HTMLDivElement;
      const subject = document.getElementById("subject");
      const subjectHidden = document.querySelector('[name="subject"]');
      const searchForm = document.getElementById("search-form");

      if (
        !(subject instanceof HTMLInputElement) ||
        !(subjectHidden instanceof HTMLInputElement) ||
        !(searchForm instanceof HTMLFormElement)
      )
        return;

      element.addEventListener("click", () => {
        document
          .querySelectorAll<HTMLElement>(
            element.getAttribute("data-target") || "",
          )
          .forEach((element) => element.remove());
        parent?.remove();

        subject.value = "";
        subjectHidden.value = "";
        searchForm.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          }),
        );
        subject.dispatchEvent(new Event("filter_options"));
      });
      element.dataset["listened"] = "true";
    });
};

const addLanguageListeners = () => {
  document
    .querySelectorAll<HTMLButtonElement>(".clear-language:not([data-listened])")
    .forEach((element) => {
      const parentNode = element.parentNode as HTMLDivElement;
      const language = document.getElementById("language");
      const languageHidden = document.querySelector('[name="language"]');
      const searchForm = document.getElementById("search-form");

      if (
        !(language instanceof HTMLInputElement) ||
        !(languageHidden instanceof HTMLInputElement) ||
        !(searchForm instanceof HTMLFormElement)
      )
        return;

      element.addEventListener("click", () => {
        document
          .querySelectorAll<HTMLElement>(
            element.getAttribute("data-target") || "",
          )
          .forEach((element) => element.remove());
        parentNode?.remove();

        language.value = "";
        languageHidden.value = "";
        searchForm.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          }),
        );
        language.dispatchEvent(new Event("filter_options"));
      });
      element.dataset["listened"] = "true";
    });
};

const addListeners = () => {
  const subjectInput = document.querySelector<HTMLInputElement>(
    "#subject:not([data-listened-in-search])",
  );

  if (subjectInput) {
    subjectInput.addEventListener("selected", () =>
      addSubject(
        noNullQuerySelector<HTMLInputElement>("[name='subject']").value,
        subjectInput.value,
      ),
    );
    subjectInput.dataset.listenedInSearch = "true";
  }

  const languageInput = document.querySelector<HTMLInputElement>(
    "#language:not([data-listened-in-search])",
  );
  if (languageInput) {
    languageInput.addEventListener("selected", () =>
      addLanguage(
        noNullQuerySelector<HTMLInputElement>("[name='language']").value,
        languageInput.value,
      ),
    );
    languageInput.dataset.listenedInSearch = "true";
  }

  const gender = document.querySelector<HTMLInputElement>("#gender");
  const search = document.querySelector<HTMLInputElement>("#search");
  const rating = document.querySelector<HTMLSelectElement>("#rating");
  const ratingHidden =
    document.querySelector<HTMLInputElement>('[name="rating"]');

  const country = document.querySelector<HTMLSelectElement>("#search_country_id");
  const countryHidden = document.querySelector<HTMLInputElement>(
    '[name="country_id"]',
  );
  const hasStudents =
    document.querySelector<HTMLSelectElement>("#has_students");
  const status = document.querySelector<HTMLSelectElement>("#status");
  const isBlocked = document.querySelector<HTMLSelectElement>("is_blocked");
  const blockOfferStatus = document.querySelector<HTMLSelectElement>(
    "#send_offers_blocked",
  );

  const isVolunteer = document.querySelector<HTMLInputElement>("#is_volunteer");
  const idVerificationStatus = document.querySelector<HTMLInputElement>(
    "#id_verification_status",
  );
  const isAvailable = document.querySelector<HTMLInputElement>("#is_available");
  const searchForm = document.querySelector<HTMLFormElement>("#search-form");
  const languagesContainer = document.querySelector<HTMLDivElement>(
    "#languages-container",
  );
  const subjectsContainer = document.querySelector<HTMLDivElement>(
    "#subjects-container",
  );

  const blockOfferStatusFilter = document.querySelector<HTMLButtonElement>(
    "#block-offer-status-filter",
  );
  const blockStatusFilter = document.querySelector<HTMLButtonElement>(
    "#block-status-filter",
  );
  const volunteerFilter =
    document.querySelector<HTMLButtonElement>("#volunteer-filter");
  const idVerificationStatusFilter = document.querySelector<HTMLButtonElement>(
    "#id-verification-status-filter",
  );
  const isAvailableFilter = document.querySelector<HTMLButtonElement>(
    "#is-available-filter",
  );
  const searchFilter = document.querySelector("#search-filter");
  const genderFilter = document.querySelector("#gender-filter");
  const studentsFilter =
    document.querySelector<HTMLButtonElement>("#students-filter");

  const ratingFilter =
    document.querySelector<HTMLButtonElement>("#rating-filter");
  const countryFilter =
    document.querySelector<HTMLButtonElement>("#country-filter");
  const statusFilter =
    document.querySelector<HTMLButtonElement>("#status-filter");

  if (!searchForm)
    throw new Error(
      "The search form, it's filters or one of its elements is missing",
    );

  searchForm.addEventListener("submit", () => {
    const filtersCollapseElement = document.querySelector("#filters-collapse");
    const filtersButton =
      document.querySelector<HTMLButtonElement>("#filters-button");

    if (filtersCollapseElement?.classList.contains("show")) {
      filtersButton?.click();
    }
  });

  document
    .querySelectorAll<HTMLButtonElement>(".clear-gender:not([data-listened])")
    .forEach((element) => {
      element.addEventListener("click", () => {
        if (gender) gender.value = "";
        genderFilter?.remove();
        searchForm.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          }),
        );
      });
      element.dataset["listened"] = "true";
    });

  addSubjectListeners();
  addLanguageListeners();

  document
    .querySelectorAll<HTMLButtonElement>(".clear-search:not([data-listened])")
    .forEach((element) => {
      element.addEventListener("click", () => {
        if (search) search.value = "";
        searchFilter?.remove();
        searchForm.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          }),
        );
      });
      element.dataset["listened"] = "true";
    });

  document
    .querySelectorAll<HTMLButtonElement>(".clear-rating:not([data-listened])")
    .forEach((element) => {
      element.addEventListener("click", () => {
        if (rating) rating.value = "";
        if (ratingHidden) ratingHidden.value = "";
        ratingFilter?.remove();
        searchForm.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          }),
        );
      });
      element.dataset["listened"] = "true";
    });

  document
    .querySelectorAll<HTMLButtonElement>(".clear-country:not([data-listened])")
    .forEach((element) => {
      element.addEventListener("click", () => {
        if (country) country.value = "";
        if (countryHidden) countryHidden.value = "";
        countryFilter?.remove();
        searchForm.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          }),
        );
        country?.dispatchEvent(new Event("filter_options"));
      });
      element.dataset["listened"] = "true";
    });

  document
    .querySelectorAll<HTMLButtonElement>(".clear-students:not([data-listened])")
    .forEach((element) => {
      element.addEventListener("click", () => {
        if (hasStudents) hasStudents.value = "";
        studentsFilter?.remove();
        searchForm.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          }),
        );
      });
      element.dataset["listened"] = "true";
    });

  document
    .querySelectorAll<HTMLButtonElement>(".clear-status:not([data-listened])")
    .forEach((element) => {
      element.addEventListener("click", () => {
        if (status) status.value = "";
        statusFilter?.remove();
        searchForm.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          }),
        );
      });
      element.dataset["listened"] = "true";
    });

  document
    .querySelectorAll<HTMLButtonElement>(
      ".clear-block-status:not([data-listened])",
    )
    .forEach((element) => {
      element.addEventListener("click", () => {
        if (isBlocked) isBlocked.value = "";
        blockStatusFilter?.remove();
        searchForm.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          }),
        );
      });
    });

  document
    .querySelectorAll<HTMLButtonElement>(
      ".clear-block-offer-status:not([data-listened])",
    )
    .forEach((element) => {
      element.addEventListener("click", () => {
        if (blockOfferStatus) blockOfferStatus.value = "";
        blockOfferStatusFilter?.remove();
        searchForm.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          }),
        );
      });
    });

  document
    .querySelectorAll<HTMLButtonElement>(
      ".clear-volunteer:not([data-listened])",
    )
    .forEach((element) => {
      element.addEventListener("click", () => {
        if (isVolunteer) isVolunteer.value = "";
        volunteerFilter?.remove();
        searchForm?.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          }),
        );
      });
    });

  document
    .querySelectorAll<HTMLButtonElement>(
      ".clear-id-verification-status:not([data-listened])",
    )
    .forEach((element) => {
      element.addEventListener("click", () => {
        if (idVerificationStatus) idVerificationStatus.value = "";
        idVerificationStatusFilter?.remove();
        searchForm?.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          }),
        );
      });
    });

  document
    .querySelectorAll<HTMLButtonElement>(
      ".clear-is-available-verification-status:not([data-listened])",
    )
    .forEach((element) => {
      element.addEventListener("click", () => {
        if (isAvailable) isAvailable.value = "";
        isAvailableFilter?.remove();
        searchForm?.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          }),
        );
      });
    });

  document
    .querySelectorAll<HTMLButtonElement>(".clear-filter")
    .forEach((element) => {
      element.addEventListener("click", () => {
        if (subjectsContainer) subjectsContainer.innerHTML = "";
        if (languagesContainer) languagesContainer.innerHTML = "";
      });
    });

  const filterOffcanvasButton = document.querySelector<HTMLButtonElement>(
    `#offcanvas-search-submit`,
  );

  if (filterOffcanvasButton) {
    const offcanvasCloseButton = noNullQuerySelector<HTMLButtonElement>(
      "#search-offcanvas-close",
    );
    filterOffcanvasButton.addEventListener("click", () => {
      offcanvasCloseButton.click();
    });
  }
};

document.addEventListener("DOMContentLoaded", () => {
  const onboardingBillNoticeModal = document.querySelector<HTMLDivElement>(
    "#onboarding-bill-notice-modal",
  );

  if (onboardingBillNoticeModal) new Modal(onboardingBillNoticeModal, {}).show;
});

document.addEventListener("DOMContentLoaded", addListeners);
document.addEventListener("ajax:success", addListeners);
